import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { RefreshCw, AlertTriangle, TrendingUp, Clock, BarChart2 } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const ESTADO_LABELS = { nuevo:'Nuevo', en_atencion:'En Atención', resuelto:'Resuelto', cerrado:'Cerrado', falsa_alarma:'Falsa Alarma' };

const CHART_COLORS = ['#00d4ff','#00e676','#ffab00','#ff6b00','#ff3d57','#9c27b0','#2196f3','#4caf50'];

const CustomTooltip = ({ active, payload, label, suffix = '' }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background:'var(--bg-elevated)', border:'1px solid var(--border-light)', borderRadius:6, padding:'8px 12px', fontSize:12 }}>
        <div style={{ color:'var(--text-muted)', marginBottom:4 }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.color || 'var(--accent)', fontWeight:600 }}>
            {p.name}: {p.value}{suffix}
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [graficas, setGraficas] = useState(null);
  const [recientes, setRecientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGraficas, setLoadingGraficas] = useState(true);
  const [dias, setDias] = useState(7);
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, eventosRes] = await Promise.all([
        api.get('/eventos/stats/resumen'),
        api.get('/eventos?limit=8&page=1')
      ]);
      setStats(statsRes.data.data);
      setRecientes(eventosRes.data.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchGraficas = useCallback(async () => {
    setLoadingGraficas(true);
    try {
      const res = await api.get(`/eventos/stats/graficas?dias=${dias}`);
      setGraficas(res.data.data);
    } catch(e) { console.error(e); }
    finally { setLoadingGraficas(false); }
  }, [dias]);

  useEffect(() => { fetchStats(); const t = setInterval(fetchStats, 30000); return () => clearInterval(t); }, [fetchStats]);
  useEffect(() => { fetchGraficas(); }, [fetchGraficas]);

  const formatFecha = (str) => {
    try { return format(parseISO(str), 'dd/MM', { locale: es }); } catch { return str; }
  };

  const formatHora = (h) => `${String(h).padStart(2,'0')}:00`;

  const formatMinutos = (m) => m < 60 ? `${m}min` : `${Math.floor(m/60)}h ${m%60}m`;

  if (loading) return <div className="loading-screen"><div className="spinner"/><span>Cargando...</span></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">Actualización automática cada 30 segundos</div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <select className="form-control" style={{ width:'auto' }} value={dias} onChange={e => setDias(parseInt(e.target.value))}>
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
          <button className="btn btn-secondary btn-sm" onClick={() => { fetchStats(); fetchGraficas(); }}><RefreshCw size={14}/> Actualizar</button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stat-grid" style={{ marginBottom:24 }}>
        <div className="stat-card danger">
          <div className="stat-value" style={{ color:'var(--danger)' }}>{stats?.nuevos ?? 0}</div>
          <div className="stat-label">Sin Atender</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-value" style={{ color:'var(--warning)' }}>{stats?.en_atencion ?? 0}</div>
          <div className="stat-label">En Atención</div>
        </div>
        <div className="stat-card success">
          <div className="stat-value" style={{ color:'var(--success)' }}>{stats?.cerrados ?? 0}</div>
          <div className="stat-label">Cerrados</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color:'var(--accent)' }}>{stats?.hoy_total ?? 0}</div>
          <div className="stat-label">Hoy</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color:'var(--text-primary)' }}>{stats?.total ?? 0}</div>
          <div className="stat-label">Total General</div>
        </div>
      </div>

      {loadingGraficas ? (
        <div className="loading-screen"><div className="spinner"/><span>Cargando gráficas...</span></div>
      ) : graficas && (
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

          {/* Fila 1: Eventos por día + Eventos por hora */}
          <div className="grid-2" style={{ gap:20 }}>

            {/* Eventos por día */}
            <div className="card">
              <div className="card-header">
                <span className="card-title"><TrendingUp size={14} style={{marginRight:6}}/>Eventos por Día</span>
                <span className="tag">{dias} días</span>
              </div>
              <div className="card-body" style={{ paddingTop:8 }}>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={graficas.porDia} margin={{ top:5, right:10, left:-20, bottom:5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="fecha" tickFormatter={formatFecha} tick={{ fontSize:10, fill:'var(--text-muted)' }} />
                    <YAxis tick={{ fontSize:10, fill:'var(--text-muted)' }} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip/>} />
                    <Line type="monotone" dataKey="total" name="Eventos" stroke="var(--accent)" strokeWidth={2} dot={{ fill:'var(--accent)', r:3 }} activeDot={{ r:5 }}/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Eventos por hora */}
            <div className="card">
              <div className="card-header">
                <span className="card-title"><Clock size={14} style={{marginRight:6}}/>Eventos por Hora del Día</span>
              </div>
              <div className="card-body" style={{ paddingTop:8 }}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={graficas.porHora} margin={{ top:5, right:10, left:-20, bottom:5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="hora" tickFormatter={h => `${h}h`} tick={{ fontSize:9, fill:'var(--text-muted)' }} interval={2} />
                    <YAxis tick={{ fontSize:10, fill:'var(--text-muted)' }} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip/>} formatter={(v) => [v, 'Eventos']} labelFormatter={h => `${h}:00 hrs`} />
                    <Bar dataKey="total" name="Eventos" fill="var(--accent-dim)" radius={[3,3,0,0]}>
                      {graficas.porHora.map((_, i) => (
                        <Cell key={i} fill={_.total > 0 ? 'var(--accent)' : 'var(--bg-elevated)'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Fila 2: Eventos por tipo + Eventos por monitorista */}
          <div className="grid-2" style={{ gap:20 }}>

            {/* Eventos por tipo - Pie */}
            <div className="card">
              <div className="card-header">
                <span className="card-title"><BarChart2 size={14} style={{marginRight:6}}/>Eventos por Tipo</span>
              </div>
              <div className="card-body" style={{ paddingTop:8 }}>
                {graficas.porTipo.length === 0 ? (
                  <div className="table-empty">Sin datos en el período</div>
                ) : (
                  <div style={{ display:'flex', gap:16, alignItems:'center' }}>
                    <ResponsiveContainer width="55%" height={220}>
                      <PieChart>
                        <Pie data={graficas.porTipo} dataKey="total" nameKey="nombre" cx="50%" cy="50%" outerRadius={85} innerRadius={45}>
                          {graficas.porTipo.map((entry, i) => (
                            <Cell key={i} fill={entry.color || CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:6, fontSize:11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ flex:1, display:'flex', flexDirection:'column', gap:6 }}>
                      {graficas.porTipo.map((item, i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ width:10, height:10, borderRadius:'50%', background:item.color || CHART_COLORS[i%CHART_COLORS.length], flexShrink:0 }}/>
                          <span style={{ fontSize:11, color:'var(--text-secondary)', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.nombre}</span>
                          <span style={{ fontSize:12, fontFamily:'var(--font-mono)', color:'var(--accent)', fontWeight:600 }}>{item.total}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Eventos por monitorista */}
            <div className="card">
              <div className="card-header">
                <span className="card-title"><AlertTriangle size={14} style={{marginRight:6}}/>Eventos por Monitorista</span>
              </div>
              <div className="card-body" style={{ paddingTop:8 }}>
                {graficas.porMonitorista.length === 0 ? (
                  <div className="table-empty">Sin eventos atendidos en el período</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={graficas.porMonitorista} layout="vertical" margin={{ top:5, right:30, left:10, bottom:5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize:10, fill:'var(--text-muted)' }} allowDecimals={false} />
                      <YAxis type="category" dataKey="nombre" tick={{ fontSize:10, fill:'var(--text-secondary)' }} width={100} />
                      <Tooltip content={<CustomTooltip/>} />
                      <Bar dataKey="total" name="Eventos" radius={[0,3,3,0]}>
                        {graficas.porMonitorista.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* Fila 3: Tiempo promedio de atención + Eventos recientes */}
          <div className="grid-2" style={{ gap:20 }}>

            {/* Tiempo promedio de atención */}
            <div className="card">
              <div className="card-header">
                <span className="card-title"><Clock size={14} style={{marginRight:6}}/>Tiempo Promedio de Atención</span>
              </div>
              <div className="card-body" style={{ paddingTop:8 }}>
                {graficas.tiempoAtencion.length === 0 ? (
                  <div className="table-empty">Sin eventos atendidos en el período</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={graficas.tiempoAtencion} margin={{ top:5, right:10, left:-10, bottom:5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="fecha" tickFormatter={formatFecha} tick={{ fontSize:10, fill:'var(--text-muted)' }} />
                      <YAxis tick={{ fontSize:10, fill:'var(--text-muted)' }} tickFormatter={m => `${m}m`} />
                      <Tooltip content={<CustomTooltip suffix=" min"/>} labelFormatter={formatFecha} />
                      <Line type="monotone" dataKey="minutos" name="Minutos" stroke="var(--success)" strokeWidth={2} dot={{ fill:'var(--success)', r:3 }}/>
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Eventos recientes */}
            <div className="card">
              <div className="card-header">
                <span className="card-title"><AlertTriangle size={14} style={{marginRight:6}}/>Eventos Recientes</span>
                <button className="btn btn-secondary btn-sm" onClick={() => navigate('/eventos')}>Ver todos</button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Folio</th><th>Tipo</th><th>Vehículo</th><th>Estado</th><th>Fecha</th></tr>
                  </thead>
                  <tbody>
                    {recientes.length === 0 && <tr><td colSpan={5} className="table-empty">Sin eventos</td></tr>}
                    {recientes.map(ev => (
                      <tr key={ev.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/eventos/${ev.id}`)}>
                        <td><span className="font-mono text-accent" style={{fontSize:10}}>{ev.folio}</span></td>
                        <td style={{ fontSize:11, maxWidth:120 }}>
                          <span style={{ display:'flex', alignItems:'center', gap:5 }}>
                            {ev.tipoEvento?.color && <span style={{ width:7, height:7, borderRadius:'50%', background:ev.tipoEvento.color, flexShrink:0 }}/>}
                            {ev.tipoEvento?.nombre || 'N/A'}
                          </span>
                        </td>
                        <td><span className="tag" style={{fontSize:10}}>{ev.vehiculo?.placas || 'N/A'}</span></td>
                        <td><span className={`badge badge-${ev.estado}`} style={{fontSize:9}}>{ESTADO_LABELS[ev.estado]}</span></td>
                        <td style={{ fontSize:10, color:'var(--text-muted)' }}>
                          {ev.fecha_alerta ? format(new Date(ev.fecha_alerta), 'dd/MM HH:mm', { locale:es }) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
